import { create } from 'zustand';
import type { AgentSessionDto, AgentMessageDto, CreateSessionRequest, SendMessageRequest } from '../types/session';
import { sessionService } from '../services/sessionService';

/**
 * Session store state interface.
 */
interface SessionState {
  // State
  currentSession: AgentSessionDto | null;
  sessions: AgentSessionDto[];
  sessionsTotalCount: number;
  messages: Map<string, AgentMessageDto[]>;
  loading: boolean;
  error: string | null;

  // Actions
  setCurrentSession: (session: AgentSessionDto | null) => void;
  loadSessions: (filters?: any) => Promise<void>;
  loadSession: (id: string) => Promise<void>;
  loadMessages: (sessionId: string, pageNumber?: number, pageSize?: number) => Promise<void>;
  createSession: (data: CreateSessionRequest) => Promise<AgentSessionDto>;
  updateSession: (id: string, data: { sessionName?: string }) => Promise<void>;
  deleteSession: (id: string) => Promise<void>;
  pauseSession: (id: string) => Promise<void>;
  resumeSession: (id: string) => Promise<void>;
  sendMessage: (sessionId: string, data: SendMessageRequest) => Promise<AgentMessageDto>;
  addMessage: (sessionId: string, message: AgentMessageDto) => void;
  clearError: () => void;
  clearMessages: (sessionId: string) => void;
}

/**
 * Session store using Zustand.
 */
export const useSessionStore = create<SessionState>((set, get) => ({
  // Initial state
  currentSession: null,
  sessions: [],
  sessionsTotalCount: 0,
  messages: new Map(),
  loading: false,
  error: null,

  // Set current session
  setCurrentSession: (session) => {
    set({ currentSession: session });
  },

  // Load sessions
  loadSessions: async (filters) => {
    set({ loading: true, error: null });
    try {
      const result = await sessionService.getSessions(filters);
      set({ sessions: result.items, sessionsTotalCount: result.totalCount, loading: false });
    } catch (error: any) {
      set({ error: error.message || 'Failed to load sessions', loading: false });
    }
  },

  // Load single session
  loadSession: async (id) => {
    set({ loading: true, error: null });
    try {
      const session = await sessionService.getSession(id);
      set({ currentSession: session, loading: false });
    } catch (error: any) {
      set({ error: error.message || 'Failed to load session', loading: false });
    }
  },

  // Load messages for a session
  loadMessages: async (sessionId, pageNumber, pageSize) => {
    set({ loading: true, error: null });
    try {
      const fetched = await sessionService.getMessages(sessionId, pageNumber, pageSize);
      const newMessages = new Map(get().messages);

      if (pageNumber && pageNumber > 1) {
        // Paginating: append older messages to the front of existing list
        const currentMessages = get().messages.get(sessionId) || [];
        newMessages.set(sessionId, [...fetched, ...currentMessages]);
      } else {
        // Initial load or refresh: replace to avoid duplicates
        newMessages.set(sessionId, fetched);
      }

      set({ messages: newMessages, loading: false });
    } catch (error: any) {
      set({ error: error.message || 'Failed to load messages', loading: false });
    }
  },

  // Create session
  createSession: async (data) => {
    set({ loading: true, error: null });
    try {
      const session = await sessionService.createSession(data);
      set((state) => ({
        sessions: [session, ...state.sessions],
        currentSession: session,
        loading: false,
      }));
      return session;
    } catch (error: any) {
      set({ error: error.message || 'Failed to create session', loading: false });
      throw error;
    }
  },

  // Update session
  updateSession: async (id, data) => {
    set({ loading: true, error: null });
    try {
      const updated = await sessionService.updateSession(id, data);
      set((state) => ({
        sessions: state.sessions.map((s) => (s.id === id ? updated : s)),
        currentSession: state.currentSession?.id === id ? updated : state.currentSession,
        loading: false,
      }));
    } catch (error: any) {
      set({ error: error.message || 'Failed to update session', loading: false });
      throw error;
    }
  },

  // Delete session
  deleteSession: async (id) => {
    set({ loading: true, error: null });
    try {
      await sessionService.deleteSession(id);
      set((state) => ({
        sessions: state.sessions.filter((s) => s.id !== id),
        currentSession: state.currentSession?.id === id ? null : state.currentSession,
        loading: false,
      }));
    } catch (error: any) {
      set({ error: error.message || 'Failed to delete session', loading: false });
      throw error;
    }
  },

  // Pause session
  pauseSession: async (id) => {
    set({ loading: true, error: null });
    try {
      const updated = await sessionService.pauseSession(id);
      set((state) => ({
        sessions: state.sessions.map((s) => (s.id === id ? updated : s)),
        currentSession: state.currentSession?.id === id ? updated : state.currentSession,
        loading: false,
      }));
    } catch (error: any) {
      set({ error: error.message || 'Failed to pause session', loading: false });
      throw error;
    }
  },

  // Resume session
  resumeSession: async (id) => {
    set({ loading: true, error: null });
    try {
      const updated = await sessionService.resumeSession(id);
      set((state) => ({
        sessions: state.sessions.map((s) => (s.id === id ? updated : s)),
        currentSession: state.currentSession?.id === id ? updated : state.currentSession,
        loading: false,
      }));
    } catch (error: any) {
      set({ error: error.message || 'Failed to resume session', loading: false });
      throw error;
    }
  },

  // Send message
  sendMessage: async (sessionId, data) => {
    set({ loading: true, error: null });
    try {
      const message = await sessionService.sendMessage(sessionId, data);
      const currentMessages = get().messages.get(sessionId) || [];
      // Avoid duplicate if message with same ID already exists
      if (currentMessages.some((m) => m.id === message.id)) {
        set({ loading: false });
        return message;
      }
      const newMessages = new Map(get().messages);
      newMessages.set(sessionId, [...currentMessages, message]);
      set({ messages: newMessages, loading: false });
      return message;
    } catch (error: any) {
      set({ error: error.message || 'Failed to send message', loading: false });
      throw error;
    }
  },

  // Add message (for real-time updates)
  addMessage: (sessionId, message) => {
    const currentMessages = get().messages.get(sessionId) || [];
    // Avoid duplicate if message with same ID already exists
    if (currentMessages.some((m) => m.id === message.id)) {
      return;
    }
    const newMessages = new Map(get().messages);
    newMessages.set(sessionId, [...currentMessages, message]);
    set({ messages: newMessages });
  },

  // Clear error
  clearError: () => {
    set({ error: null });
  },

  // Clear messages for a session
  clearMessages: (sessionId) => {
    const newMessages = new Map(get().messages);
    newMessages.delete(sessionId);
    set({ messages: newMessages });
  },
}));
